import { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { FileText, Trash2, UploadCloud, File as FileIcon, Loader2 } from "lucide-react";

const DOC_TYPES = ["won_proposal", "capability_statement", "past_proposal", "resume", "qc_plan", "safety_plan", "insurance_cert", "bond_letter", "other"];
const ACCEPT = ".pdf,.docx,.doc,.txt,.png,.jpg,.jpeg,.webp";

export default function Documents() {
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [docType, setDocType] = useState("capability_statement");
  const [tags, setTags] = useState("");
  const inputRef = useRef(null);

  const load = () => api.get("/documents").then(r => setDocs(r.data));
  useEffect(() => { load(); }, []);

  const uploadFile = async (file) => {
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) { toast.error("File too large (max 25MB)"); return; }
    setUploading(true); setProgress(0);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("doc_type", docType);
    fd.append("tags", tags);
    try {
      await api.post("/documents/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => setProgress(Math.round((e.loaded * 100) / (e.total || 1))),
      });
      toast.success(`Uploaded ${file.name}`);
      setTags("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false); setProgress(0);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    const f = e.dataTransfer.files?.[0];
    if (f) uploadFile(f);
  };

  const remove = async (id) => {
    try { await api.del(`/documents/${id}`); load(); toast.success("Deleted"); } catch { toast.error("Failed"); }
  };

  const humanSize = (b) => b ? `${(b / 1024).toFixed(1)} KB` : "";

  return (
    <div className="p-8 stagger-in max-w-5xl" data-testid="documents-page">
      <div className="mb-8">
        <div className="mono-label text-cyan-400 mb-2">// COMPANY LIBRARY</div>
        <h1 className="font-display font-black text-4xl tracking-tight">Documents</h1>
        <p className="text-slate-400 mt-2">{docs.length} indexed · text extracted and used by the AI</p>
      </div>

      <div className="grid md:grid-cols-3 gap-4 mb-4">
        <div className="md:col-span-1">
          <div className="mono-label text-slate-400 mb-2">Type</div>
          <select value={docType} onChange={(e) => setDocType(e.target.value)} className="w-full bg-[#0D1424] border border-white/10 focus:border-cyan-400 px-3 py-2.5 text-sm outline-none" data-testid="upload-type">
            {DOC_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
          </select>
        </div>
        <div className="md:col-span-2">
          <div className="mono-label text-slate-400 mb-2">Tags (comma-separated)</div>
          <input value={tags} onChange={(e) => setTags(e.target.value)} placeholder="roofing, va, past-performance" className="w-full bg-[#0D1424] border border-white/10 focus:border-cyan-400 px-3 py-2.5 text-sm outline-none" data-testid="upload-tags" />
        </div>
      </div>

      <div onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }} onDrop={onDrop} className={`bg-[#0D1424] border-2 border-dashed p-10 mb-6 text-center transition-colors ${uploading ? "border-cyan-400" : "border-white/10 hover:border-cyan-400/50"}`} data-testid="dropzone">
        <input ref={inputRef} type="file" accept={ACCEPT} onChange={(e) => uploadFile(e.target.files?.[0])} className="hidden" data-testid="file-input" />
        {uploading ? (
          <div>
            <Loader2 className="w-8 h-8 text-cyan-400 mx-auto mb-4 animate-spin" />
            <div className="mono-label text-cyan-400 mb-3">UPLOADING · {progress}%</div>
            <div className="w-full max-w-md mx-auto h-1 bg-white/5"><div className="h-full bg-cyan-400 transition-all" style={{ width: `${progress}%` }} /></div>
          </div>
        ) : (
          <div>
            <UploadCloud className="w-10 h-10 text-cyan-400 mx-auto mb-4" strokeWidth={1.5} />
            <div className="font-display font-bold text-lg mb-2">Drop a file here</div>
            <div className="text-sm text-slate-400 mb-4">PDF, DOCX, TXT, PNG, JPG · up to 25MB</div>
            <button onClick={() => inputRef.current?.click()} className="bg-cyan-400 text-[#070B14] px-5 py-2 mono-label hover:bg-cyan-300" data-testid="choose-file">CHOOSE FILE</button>
          </div>
        )}
      </div>

      <div className="bg-[#0D1424] border border-white/10">
        {docs.length === 0 ? <div className="p-12 text-center text-slate-500"><FileText className="w-10 h-10 mx-auto mb-3 text-slate-600" />No documents yet.</div>
          : docs.map(d => (
            <div key={d.doc_id} className="p-5 border-b border-white/5 flex items-center justify-between gap-4" data-testid={`doc-${d.doc_id}`}>
              <div className="min-w-0 flex-1 flex items-start gap-4">
                <FileIcon className="w-6 h-6 text-cyan-400 shrink-0 mt-0.5" strokeWidth={1.5} />
                <div className="min-w-0 flex-1">
                  <div className="mono-label text-cyan-400 mb-1">{d.doc_type?.replace(/_/g, " ")}</div>
                  <div className="font-medium truncate">{d.name}</div>
                  <div className="text-xs text-slate-500 mt-1 truncate">{humanSize(d.size)} · {d.content ? `${d.content.length} chars indexed` : "text not extracted"}{d.tags?.length ? ` · ${d.tags.join(", ")}` : ""}</div>
                </div>
              </div>
              <button onClick={() => remove(d.doc_id)} className="text-slate-500 hover:text-red-400 transition-colors p-2" data-testid={`del-${d.doc_id}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
      </div>
    </div>
  );
}
